import {
  RECHARGE_RUN_STATUS,
  RENEWAL_DISCOUNT_APPLICATION_STATUS,
  RENEWAL_DISCOUNT_RUN_KIND,
  formatParisDateTime,
  renewalDiscountApplicationStatusSchema,
  type RenewalDiscountRun,
} from '@flux/shared';
import { Loader2 } from 'lucide-react';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { formatCount } from '../subscriptions/money.js';

/** Compteurs d'une exécution, libellés par les statuts clients quand c'en sont. */
const OTHER_COUNT_LABELS: Readonly<Record<string, string>> = {
  customers: 'clients examinés',
  charges: 'renouvellements lus',
  unchanged: 'inchangés',
  skipped: 'sans renouvellement visé',
  notGenerated: 'renouvellements à venir',
  absent: 'déjà sans remise',
  codesDisabled: 'codes désactivés',
  errors: 'erreurs',
};

export function runCountsLabel(counts: Record<string, number>): string {
  return Object.entries(counts)
    .filter(([, value]) => value > 0)
    .map(([key, value]) => {
      const status = renewalDiscountApplicationStatusSchema.safeParse(key);
      const label = status.success ? RENEWAL_DISCOUNT_APPLICATION_STATUS[status.data].label.toLowerCase() : (OTHER_COUNT_LABELS[key] ?? key);
      return `${formatCount(value)} ${label}`;
    })
    .join(' · ');
}

/** Dernière exécution de chaque sorte : aperçu, passe initiale, réconciliation, retrait (`07` §6.4). */
export function RenewalRunsSection({ runs }: { runs: readonly RenewalDiscountRun[] }): JSX.Element | null {
  if (runs.length === 0) {
    return null;
  }
  return (
    <PageSection title="Dernières exécutions">
      <ul className="flex flex-col divide-y divide-border">
        {runs.map((run) => (
          <li key={run.id} className="flex flex-col gap-1 py-3 text-sm first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{RENEWAL_DISCOUNT_RUN_KIND[run.kind].label}</span>
              <StatusBadge registry={RECHARGE_RUN_STATUS} code={run.status} />
              <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                {formatParisDateTime(new Date(run.finishedAt ?? run.startedAt))}
              </span>
            </div>
            <p className="flex items-center gap-2 tabular-nums text-muted-foreground">
              {run.status === 'running' ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
              {runCountsLabel(run.counts) || (run.status === 'running' ? 'En cours…' : 'Aucun client concerné')}
            </p>
            {run.error === null ? null : <p className="text-status-red-fg">{run.error}</p>}
          </li>
        ))}
      </ul>
    </PageSection>
  );
}
